import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import UserAvatar from './UserAvatar';
import L10VideoNotes from './L10VideoNotes';
import AssignPresenterDialog from './AssignPresenterDialog';
import {
  Video, VideoOff, Mic, MicOff, Phone, PhoneOff,
  Users, FileText, Maximize2, Minimize2, PanelRightClose, PanelRight,
  Volume2, VolumeX, Hand, Grid3X3, Circle, X, Crown,
  ExternalLink, Monitor, MonitorOff, Presentation, UserCheck
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Video Tile Component - Extracted for performance
 */
const VideoTile = ({ 
  participant, 
  isLarge = false, 
  isCurrentUser = false,
  state = { micOn: true, camOn: true, isSpeaking: false },
  isSpeaker = false,
  hasRaisedHand = false,
  isPresenter = false,
  isScreenSharing = false
}) => {
  return (
    <div
      className={`relative rounded-lg overflow-hidden transition-all ${
        isLarge ? 'aspect-video' : 'aspect-square'
      } ${
        isScreenSharing ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-slate-900' :
        isPresenter ? 'ring-2 ring-orange-500 ring-offset-2 ring-offset-slate-900' :
        isSpeaker ? 'ring-2 ring-green-500 ring-offset-2 ring-offset-slate-900' : ''
      } ${state.camOn ? 'bg-slate-800' : 'bg-slate-850'}`}
    >
      {/* Screen share overlay */}
      {isScreenSharing && (
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/50 to-slate-900 flex items-center justify-center z-10">
          <div className="text-center">
            <Monitor className="h-10 w-10 mx-auto text-blue-400 mb-2" />
            <span className="text-xs text-blue-300">Screen Sharing</span>
          </div>
        </div>
      )}
      
      {/* Video/Avatar */}
      {!isScreenSharing && (
        <div className={`absolute inset-0 ${state.camOn ? 'bg-gradient-to-br from-slate-700 to-slate-800' : 'bg-slate-850'} flex items-center justify-center`}>
          <UserAvatar
            user={participant}
            size={isLarge ? 'xl' : 'lg'}
            showStatus={false}
          />
        </div>
      )}
      
      {/* Speaking indicator */}
      {state.isSpeaking && !isScreenSharing && (
        <div className="absolute inset-0 border-2 border-green-500 rounded-lg pointer-events-none animate-pulse" />
      )}
      
      {/* Top badges */}
      <div className="absolute top-2 left-2 flex items-center gap-1 flex-wrap z-20">
        {participant.role === 'facilitator' && (
          <Badge className="bg-purple-600/80 text-[10px] py-0 px-1">
            <Crown className="h-2.5 w-2.5" />
          </Badge>
        )}
        {isPresenter && (
          <Badge className="bg-orange-500/80 text-[10px] py-0 px-1">
            <Presentation className="h-2.5 w-2.5 mr-0.5" />
            PRESENTER
          </Badge>
        )}
        {isScreenSharing && (
          <Badge className="bg-blue-500/80 text-[10px] py-0 px-1">
            <Monitor className="h-2.5 w-2.5 mr-0.5" />
            SHARING
          </Badge>
        )}
        {hasRaisedHand && (
          <Badge className="bg-yellow-500/80 text-[10px] py-0 px-1">
            <Hand className="h-2.5 w-2.5" />
          </Badge>
        )}
      </div>
      
      {/* Bottom info bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 z-20">
        <div className="flex items-center justify-between">
          <span className="text-xs text-white font-medium truncate">
            {participant.display_name || participant.email?.split('@')[0]}
            {isCurrentUser && ' (You)'}
          </span>
          <div className="flex items-center gap-1">
            {!state.micOn && <MicOff className="h-3 w-3 text-red-500" />}
            {!state.camOn && <VideoOff className="h-3 w-3 text-red-500" />}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * L10 Video Panel - Modern video meeting experience
 * Meet/Zoom-style UI embedded in L10 Meeting Room
 */
const L10VideoPanel = ({
  meetingId,
  isJoined = false,
  onJoin,
  onLeave,
  participants = [],
  currentUserId,
  currentSection,
  isFacilitator = false,
  copilotEnabled = false,
  onNotesUpdate,
  videoNotes = [],
  currentPresenterId = null,
  onPresenterChange,
  className = ""
}) => {
  // Video state
  const [micEnabled, setMicEnabled] = useState(true);
  const [camEnabled, setCamEnabled] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('video');
  const [isMuted, setIsMuted] = useState(false);
  
  // Participant video states (simulated for mock mode)
  const [participantStates, setParticipantStates] = useState({});
  const [activeSpeakerId, setActiveSpeakerId] = useState(null);
  const [raisedHands, setRaisedHands] = useState([]);
  
  // View mode
  const [viewMode, setViewMode] = useState('gallery');
  
  // Screen share state
  const [screenShareState, setScreenShareState] = useState({ isSharing: false, sharedByUserId: null });
  
  // Presenter dialog
  const [showPresenterDialog, setShowPresenterDialog] = useState(false);
  const [localPresenterId, setLocalPresenterId] = useState(currentPresenterId);
  
  // Popout window reference
  const [popoutWindow, setPopoutWindow] = useState(null);
  
  // Update local presenter when prop changes
  useEffect(() => {
    setLocalPresenterId(currentPresenterId);
  }, [currentPresenterId]);
  
  // Initialize participant states on mount
  useEffect(() => {
    if (participants.length === 0) return;
    
    const initialStates = {};
    participants.forEach(p => {
      initialStates[p.user_id] = {
        micOn: Math.random() > 0.3,
        camOn: Math.random() > 0.4,
        isSpeaking: false
      };
    });
    setParticipantStates(initialStates);
  }, [participants]);
  
  // Simulate active speaker detection
  useEffect(() => {
    if (participants.length === 0) return;
    
    const speakerInterval = setInterval(() => {
      const randomIdx = Math.floor(Math.random() * participants.length);
      const randomParticipant = participants[randomIdx];
      if (randomParticipant) {
        setActiveSpeakerId(randomParticipant.user_id);
      }
    }, 4000);
    
    return () => clearInterval(speakerInterval);
  }, [participants]);
  
  // Listen for sync events from popout window
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'l10_video_sync') {
        try {
          const event = JSON.parse(e.newValue);
          if (event.meetingId !== meetingId) return;
          
          switch (event.key) {
            case 'mic':
              setMicEnabled(event.value);
              break;
            case 'cam':
              setCamEnabled(event.value);
              break;
            case 'leave':
              onLeave?.();
              break;
            case 'screenShare':
              setScreenShareState(event.value);
              break;
            case 'presenter':
              setLocalPresenterId(event.value);
              onPresenterChange?.(event.value);
              break;
            default:
              break;
          }
        } catch (e) {
          // Ignore JSON parse errors from malformed storage events
        }
      }
    };
    
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [meetingId, onLeave, onPresenterChange]);
  
  // Sync to popout window
  const syncToPopout = useCallback((key, value) => {
    const event = { meetingId, key, value, timestamp: Date.now() };
    localStorage.setItem('l10_video_sync', JSON.stringify(event));
  }, [meetingId]);
  
  // Handle mic toggle
  const handleMicToggle = useCallback(() => {
    setMicEnabled(prev => {
      const newValue = !prev;
      syncToPopout('mic', newValue);
      toast.info(newValue ? 'Microphone unmuted' : 'Microphone muted');
      return newValue;
    });
  }, [syncToPopout]);
  
  // Handle camera toggle
  const handleCamToggle = useCallback(() => {
    setCamEnabled(prev => {
      const newValue = !prev;
      syncToPopout('cam', newValue);
      toast.info(newValue ? 'Camera on' : 'Camera off');
      return newValue;
    });
  }, [syncToPopout]);
  
  // Handle screen share
  const handleScreenShare = useCallback(async () => {
    const newSharing = !screenShareState.isSharing;
    const newState = {
      isSharing: newSharing,
      sharedByUserId: newSharing ? currentUserId : null
    };
    setScreenShareState(newState);
    syncToPopout('screenShare', newState);
    
    // Update backend with new dedicated endpoint
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/eos/meetings/${meetingId}/screen-share`, {
        is_sharing: newSharing,
        shared_by_user_id: newSharing ? currentUserId : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error updating screen share:', error);
    }
    
    toast.info(newSharing ? 'Screen sharing started' : 'Screen sharing stopped');
  }, [screenShareState, currentUserId, meetingId, syncToPopout]);
  
  // Handle leave video
  const handleLeave = useCallback(() => {
    syncToPopout('leave', true);
    if (popoutWindow && !popoutWindow.closed) {
      popoutWindow.close();
    }
    onLeave?.();
    toast.info('Left video call');
  }, [onLeave, popoutWindow, syncToPopout]);
  
  // Handle raise hand
  const handleRaiseHand = useCallback(() => {
    setRaisedHands(prev => {
      if (prev.includes(currentUserId)) {
        toast.info('Hand lowered');
        return prev.filter(id => id !== currentUserId);
      } else {
        toast.info('Hand raised');
        return [...prev, currentUserId];
      }
    });
  }, [currentUserId]);
  
  // Handle mute all (facilitator only)
  const handleMuteAll = useCallback(() => {
    if (!isFacilitator) return;
    setParticipantStates(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(id => {
        if (id !== currentUserId) {
          updated[id] = { ...updated[id], micOn: false };
        }
      });
      return updated;
    });
    toast.success('All participants muted');
  }, [isFacilitator, currentUserId]);
  
  // Handle end call for all (facilitator only)
  const handleEndForAll = useCallback(() => {
    if (!isFacilitator) return;
    handleLeave();
    toast.success('Video ended for all participants');
  }, [isFacilitator, handleLeave]);
  
  // Handle popout
  const handlePopout = useCallback(() => {
    const width = 1200;
    const height = 800;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    
    const popout = window.open(
      `/eos/l10-meetings/${meetingId}/video-popout?mic=${micEnabled}&cam=${camEnabled}`,
      'L10VideoPopout',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,toolbar=no,menubar=no,location=no,status=no`
    );
    
    if (popout) {
      setPopoutWindow(popout);
      toast.success('Video opened in new window');
    } else {
      toast.error('Failed to open popout. Check popup blocker settings.');
    }
  }, [meetingId, micEnabled, camEnabled]);
  
  // Handle presenter change from dialog
  const handlePresenterDialogChange = useCallback((newPresenterId) => {
    setLocalPresenterId(newPresenterId);
    syncToPopout('presenter', newPresenterId);
    onPresenterChange?.(newPresenterId);
  }, [syncToPopout, onPresenterChange]);
  
  // Sort participants - screen sharer first, then presenter, then active speaker
  const sortedParticipants = useMemo(() => {
    return [...participants].sort((a, b) => {
      // Screen sharer first
      if (a.user_id === screenShareState.sharedByUserId) return -1;
      if (b.user_id === screenShareState.sharedByUserId) return 1;
      // Then presenter
      if (a.user_id === localPresenterId) return -1;
      if (b.user_id === localPresenterId) return 1;
      // Then active speaker
      if (a.user_id === activeSpeakerId) return -1;
      if (b.user_id === activeSpeakerId) return 1;
      // Then raised hands
      if (raisedHands.includes(a.user_id)) return -1;
      if (raisedHands.includes(b.user_id)) return 1;
      // Then facilitator
      if (a.role === 'facilitator') return -1;
      if (b.role === 'facilitator') return 1;
      return 0;
    });
  }, [participants, activeSpeakerId, raisedHands, localPresenterId, screenShareState]);
  
  // Not joined state
  if (!isJoined) {
    return (
      <Card className={`bg-slate-900 border-slate-800 ${className}`}>
        <CardContent className="py-12 text-center">
          <Video className="h-16 w-16 mx-auto mb-4 text-slate-600" />
          <h3 className="text-lg font-semibold text-white mb-2">Video Not Connected</h3>
          <p className="text-sm text-slate-400 mb-6">Join video to see your team and collaborate in real-time</p>
          <Button onClick={onJoin} className="bg-green-600 hover:bg-green-700">
            <Video className="h-4 w-4 mr-2" />
            Join Video
          </Button>
        </CardContent>
      </Card>
    );
  }
  
  // Collapsed state
  if (isPanelCollapsed) {
    return (
      <div className={`flex items-center gap-2 p-2 bg-slate-900 rounded-lg border border-slate-800 ${className}`}>
        <div className="flex items-center gap-2">
          <Badge className="bg-green-600">LIVE</Badge>
          <span className="text-sm text-slate-300">{participants.length} in call</span>
          {screenShareState.isSharing && (
            <Badge className="bg-blue-600 text-[10px]">
              <Monitor className="h-2.5 w-2.5 mr-0.5" />
              Sharing
            </Badge>
          )}
        </div>
        
        {/* Mini controls */}
        <div className="flex items-center gap-1 ml-auto">
          <Button
            variant="ghost"
            size="icon"
            className={`h-8 w-8 ${micEnabled ? 'text-slate-300' : 'text-red-500 bg-red-500/20'}`}
            onClick={handleMicToggle}
          >
            {micEnabled ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={`h-8 w-8 ${camEnabled ? 'text-slate-300' : 'text-red-500 bg-red-500/20'}`}
            onClick={handleCamToggle}
          >
            {camEnabled ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-slate-300"
            onClick={() => setIsPanelCollapsed(false)}
          >
            <PanelRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Card className={`bg-slate-900 border-slate-800 flex flex-col ${
        isExpanded ? 'fixed inset-4 z-50' : ''
      } ${className}`}>
        {/* Header */}
        <CardHeader className="py-2 px-3 border-b border-slate-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge className="bg-green-600">
                <Circle className="h-2 w-2 mr-1 fill-current animate-pulse" />
                LIVE
              </Badge>
              <span className="text-sm text-slate-300">{participants.length} participants</span>
              {screenShareState.isSharing && (
                <Badge className="bg-blue-600 text-[10px]">
                  <Monitor className="h-2.5 w-2.5 mr-0.5" />
                  Sharing
                </Badge>
              )}
            </div>
            
            <div className="flex items-center gap-1">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-slate-400 hover:text-white"
                      onClick={() => setViewMode(viewMode === 'gallery' ? 'speaker' : 'gallery')}
                    >
                      <Grid3X3 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Switch view</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-slate-400 hover:text-white"
                      onClick={handlePopout}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Pop out to new window</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-slate-400 hover:text-white"
                      onClick={() => setIsExpanded(!isExpanded)}
                    >
                      {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{isExpanded ? 'Exit focus mode' : 'Focus mode'}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-slate-400 hover:text-white"
                onClick={() => setIsPanelCollapsed(true)}
              >
                <PanelRightClose className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        
        {/* Screen Share Banner */}
        {screenShareState.isSharing && (
          <div className="bg-blue-900/50 px-3 py-1.5 flex items-center justify-between">
            <div className="flex items-center gap-2 text-blue-300 text-xs">
              <Monitor className="h-3.5 w-3.5" />
              <span>
                {screenShareState.sharedByUserId === currentUserId 
                  ? 'You are sharing your screen' 
                  : `${participants.find(p => p.user_id === screenShareState.sharedByUserId)?.display_name || 'Someone'} is sharing`
                }
              </span>
            </div>
            {screenShareState.sharedByUserId === currentUserId && (
              <Button size="sm" variant="destructive" className="h-6 text-xs" onClick={handleScreenShare}>
                Stop
              </Button>
            )}
          </div>
        )}
        
        <CardContent className="flex-1 p-0 overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
            <TabsList className="w-full rounded-none border-b border-slate-800 bg-slate-900/50">
              <TabsTrigger value="video" className="flex-1 text-xs data-[state=active]:bg-slate-800">
                <Video className="h-3 w-3 mr-1" />
                Video
              </TabsTrigger>
              <TabsTrigger value="participants" className="flex-1 text-xs data-[state=active]:bg-slate-800">
                <Users className="h-3 w-3 mr-1" />
                People ({participants.length})
              </TabsTrigger>
              <TabsTrigger value="notes" className="flex-1 text-xs data-[state=active]:bg-slate-800">
                <FileText className="h-3 w-3 mr-1" />
                Notes
              </TabsTrigger>
            </TabsList>
            
            {/* Video Grid Tab */}
            <TabsContent value="video" className="flex-1 m-0 p-2">
              <div className={`grid gap-2 h-full ${
                viewMode === 'speaker' 
                  ? 'grid-cols-1' 
                  : participants.length <= 2 
                    ? 'grid-cols-1' 
                    : participants.length <= 4 
                      ? 'grid-cols-2' 
                      : 'grid-cols-3'
              }`}>
                {viewMode === 'speaker' ? (
                  // Speaker view - large active speaker + small tiles
                  <>
                    {sortedParticipants[0] && (
                      <VideoTile
                        participant={sortedParticipants[0]}
                        isLarge={true}
                        isCurrentUser={sortedParticipants[0].user_id === currentUserId}
                        state={participantStates[sortedParticipants[0].user_id] || { micOn: true, camOn: true, isSpeaking: false }}
                        isSpeaker={sortedParticipants[0].user_id === activeSpeakerId}
                        hasRaisedHand={raisedHands.includes(sortedParticipants[0].user_id)}
                        isPresenter={sortedParticipants[0].user_id === localPresenterId}
                        isScreenSharing={sortedParticipants[0].user_id === screenShareState.sharedByUserId}
                      />
                    )}
                    <div className="flex gap-1 overflow-x-auto">
                      {sortedParticipants.slice(1, 5).map(p => (
                        <div key={p.user_id} className="w-20 flex-shrink-0">
                          <VideoTile
                            participant={p}
                            isCurrentUser={p.user_id === currentUserId}
                            state={participantStates[p.user_id] || { micOn: true, camOn: true, isSpeaking: false }}
                            isSpeaker={p.user_id === activeSpeakerId}
                            hasRaisedHand={raisedHands.includes(p.user_id)}
                            isPresenter={p.user_id === localPresenterId}
                            isScreenSharing={p.user_id === screenShareState.sharedByUserId}
                          />
                        </div>
                      ))}
                      {sortedParticipants.length > 5 && (
                        <div className="w-20 h-20 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400 text-xs">
                          +{sortedParticipants.length - 5}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  // Gallery view
                  sortedParticipants.slice(0, 9).map(p => (
                    <VideoTile
                      key={p.user_id}
                      participant={p}
                      isCurrentUser={p.user_id === currentUserId}
                      state={participantStates[p.user_id] || { micOn: true, camOn: true, isSpeaking: false }}
                      isSpeaker={p.user_id === activeSpeakerId}
                      hasRaisedHand={raisedHands.includes(p.user_id)}
                      isPresenter={p.user_id === localPresenterId}
                      isScreenSharing={p.user_id === screenShareState.sharedByUserId}
                    />
                  ))
                )}
              </div>
            </TabsContent>
            
            {/* Participants Tab */}
            <TabsContent value="participants" className="flex-1 m-0">
              <ScrollArea className="h-full">
                <div className="p-2 space-y-1">
                  {/* Assign Presenter Button (Facilitator only) */}
                  {isFacilitator && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mb-2 border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                      onClick={() => setShowPresenterDialog(true)}
                    >
                      <UserCheck className="h-4 w-4 mr-2" />
                      Assign Presenter
                    </Button>
                  )}
                  
                  {sortedParticipants.map(p => {
                    const state = participantStates[p.user_id] || { micOn: true, camOn: true, isSpeaking: false };
                    const isSpeaker = p.user_id === activeSpeakerId;
                    const isPresenter = p.user_id === localPresenterId;
                    const isScreenSharing = p.user_id === screenShareState.sharedByUserId;
                    
                    return (
                      <div
                        key={p.user_id}
                        className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                          isScreenSharing ? 'bg-blue-900/30 border border-blue-800' :
                          isPresenter ? 'bg-orange-900/30 border border-orange-800' :
                          isSpeaker ? 'bg-green-900/30 border border-green-800' : 'hover:bg-slate-800'
                        }`}
                      >
                        <UserAvatar user={p} size="sm" />
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1 flex-wrap">
                            <span className="text-sm text-white truncate">
                              {p.display_name || p.email?.split('@')[0]}
                            </span>
                            {p.user_id === currentUserId && (
                              <Badge variant="outline" className="text-[10px] py-0 border-slate-600">(You)</Badge>
                            )}
                            {p.role === 'facilitator' && (
                              <Crown className="h-3 w-3 text-purple-400" />
                            )}
                            {isPresenter && (
                              <Badge className="bg-orange-500 text-[10px] py-0 px-1">
                                <Presentation className="h-2 w-2 mr-0.5" />
                                PRESENTER
                              </Badge>
                            )}
                            {isScreenSharing && (
                              <Badge className="bg-blue-500 text-[10px] py-0 px-1">
                                <Monitor className="h-2 w-2 mr-0.5" />
                                SHARING
                              </Badge>
                            )}
                          </div>
                          {state.isSpeaking && (
                            <span className="text-xs text-green-400">Speaking...</span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-1">
                          {raisedHands.includes(p.user_id) && (
                            <Hand className="h-4 w-4 text-yellow-500" />
                          )}
                          <div className={`p-1 rounded ${state.micOn ? 'text-slate-400' : 'text-red-500 bg-red-500/20'}`}>
                            {state.micOn ? <Mic className="h-3 w-3" /> : <MicOff className="h-3 w-3" />}
                          </div>
                          <div className={`p-1 rounded ${state.camOn ? 'text-slate-400' : 'text-red-500 bg-red-500/20'}`}>
                            {state.camOn ? <Video className="h-3 w-3" /> : <VideoOff className="h-3 w-3" />}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </TabsContent>
            
            {/* Notes Tab */}
            <TabsContent value="notes" className="flex-1 m-0 overflow-hidden">
              <L10VideoNotes
                meetingId={meetingId}
                currentSection={currentSection}
                currentUserId={currentUserId}
                copilotEnabled={copilotEnabled}
                notes={videoNotes}
                onNotesUpdate={onNotesUpdate}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
        
        {/* Video Controls Bar */}
        <div className="p-2 border-t border-slate-800 bg-slate-950">
          <div className="flex items-center justify-center gap-2">
            {/* Mic */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={micEnabled ? 'secondary' : 'destructive'}
                    size="icon"
                    className="h-10 w-10 rounded-full"
                    onClick={handleMicToggle}
                  >
                    {micEnabled ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{micEnabled ? 'Mute' : 'Unmute'}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {/* Camera */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={camEnabled ? 'secondary' : 'destructive'}
                    size="icon"
                    className="h-10 w-10 rounded-full"
                    onClick={handleCamToggle}
                  >
                    {camEnabled ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{camEnabled ? 'Turn off camera' : 'Turn on camera'}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {/* Screen Share */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={screenShareState.isSharing && screenShareState.sharedByUserId === currentUserId ? 'default' : 'ghost'}
                    size="icon"
                    className={`h-10 w-10 rounded-full ${
                      screenShareState.isSharing && screenShareState.sharedByUserId === currentUserId
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'text-slate-400 hover:text-white'
                    }`}
                    onClick={handleScreenShare}
                  >
                    {screenShareState.isSharing && screenShareState.sharedByUserId === currentUserId 
                      ? <MonitorOff className="h-5 w-5" /> 
                      : <Monitor className="h-5 w-5" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {screenShareState.isSharing && screenShareState.sharedByUserId === currentUserId
                    ? 'Stop sharing'
                    : 'Share screen'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {/* Volume */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 rounded-full text-slate-400 hover:text-white"
                    onClick={() => setIsMuted(!isMuted)}
                  >
                    {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{isMuted ? 'Unmute audio' : 'Mute audio'}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {/* Raise Hand */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={raisedHands.includes(currentUserId) ? 'default' : 'ghost'}
                    size="icon"
                    className={`h-10 w-10 rounded-full ${
                      raisedHands.includes(currentUserId) 
                        ? 'bg-yellow-500 hover:bg-yellow-600 text-black' 
                        : 'text-slate-400 hover:text-white'
                    }`}
                    onClick={handleRaiseHand}
                  >
                    <Hand className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{raisedHands.includes(currentUserId) ? 'Lower hand' : 'Raise hand'}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {/* Participants */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 rounded-full text-slate-400 hover:text-white"
                    onClick={() => setActiveTab('participants')}
                  >
                    <Users className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Participants</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {/* Notes */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 rounded-full text-slate-400 hover:text-white"
                    onClick={() => setActiveTab('notes')}
                  >
                    <FileText className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Meeting Notes</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {/* Assign Presenter (Facilitator only) */}
            {isFacilitator && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 rounded-full text-orange-400 hover:text-orange-300 hover:bg-orange-900/30"
                      onClick={() => setShowPresenterDialog(true)}
                    >
                      <Presentation className="h-5 w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Assign Presenter</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            
            {/* Mute All (Facilitator only) */}
            {isFacilitator && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 rounded-full text-slate-400 hover:text-white relative"
                      onClick={handleMuteAll}
                    >
                      <Volume2 className="h-5 w-5" />
                      <X className="h-3 w-3 absolute -bottom-0.5 -right-0.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Mute all</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            
            {/* Pop-out */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 rounded-full text-slate-400 hover:text-white"
                    onClick={handlePopout}
                  >
                    <ExternalLink className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Pop out video</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {/* Leave */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="destructive"
                    size="icon"
                    className="h-10 w-10 rounded-full"
                    onClick={handleLeave}
                  >
                    <PhoneOff className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Leave call</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {/* End for all (facilitator) */}
            {isFacilitator && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 rounded-full text-red-400 hover:text-red-300 hover:bg-red-900/30"
                      onClick={handleEndForAll}
                    >
                      <Phone className="h-5 w-5 rotate-[135deg]" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>End for all</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      </Card>
      
      {/* Assign Presenter Dialog */}
      <AssignPresenterDialog
        open={showPresenterDialog}
        onOpenChange={setShowPresenterDialog}
        meetingId={meetingId}
        participants={participants}
        currentPresenterId={localPresenterId}
        onPresenterChange={handlePresenterDialogChange}
      />
    </>
  );
};

export default L10VideoPanel;
